import {
    Alert,
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
  
  const OnBoarding_SMSLink = ({ mobile, onVerificationSuccess }) => {
    const [verificationCode, setVerificationCode] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isWaitingForSMS, setIsWaitingForSMS] = useState(false);
  
    // State to handle the cooldown timer
    const [cooldownTime, setCooldownTime] = useState(30); // Start with 30 seconds
    const [isCooldownActive, setIsCooldownActive] = useState(false); // To track cooldown status
    const [prevCooldownTime, setPrevCooldownTime] = useState(30); // To store the last cooldown time value
  
    // Effect to handle cooldown timer
    useEffect(() => {
      let timer;
  
      if (isCooldownActive && cooldownTime > 0) {
        timer = setTimeout(() => {
          setCooldownTime((prevTime) => prevTime - 1);
        }, 1000);
      } else if (cooldownTime === 0) {
        setIsCooldownActive(false); // Deactivate cooldown when time reaches 0
      }
  
      return () => clearTimeout(timer); // Cleanup the timer on component unmount
    }, [isCooldownActive, cooldownTime]);
  
    // Request the backend to send the SMS
    const sendVerificationSMS = async () => {
      if (isCooldownActive) {
        setAlertType("warning");
        setAlertMessage(
          `Please wait ${cooldownTime} seconds before trying again.`
        );
        return;
      }
  
      setIsWaitingForSMS(true);
      setIsCooldownActive(true); // Start cooldown
  
      // Increase the cooldown time by 1/3 after each SMS
      const increasedCooldownTime = Math.floor(prevCooldownTime * 4 / 3); // Increase by 1/3
  
      // Set the new cooldown time and store the updated value
      setCooldownTime(increasedCooldownTime);
      setPrevCooldownTime(increasedCooldownTime); // Store the updated cooldown time for next click
  
      try {
        // Send the mobile number to the backend to trigger SMS sending
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/verificationSMS/send`, // Backend endpoint
          { mobile }, // Send the mobile number
          {
            headers: {
              "x-api-key": process.env.REACT_APP_BACKEND_API_KEY, // API key for authorization
            },
          }
        );
  
        if (response.data.success) {
          setIsWaitingForSMS(false);
          setAlertType("info");
          setAlertMessage(
            "The verification code has been sent. Please enter the code."
          );
        } else {
          setIsWaitingForSMS(false);
          setAlertType("error");
          setAlertMessage("Failed to send SMS. Please try again.");
        }
      } catch (error) {
        setIsWaitingForSMS(false);
        setAlertType("error");
        setAlertMessage("Error sending SMS. Please try again.");
      }
    };
  
    // Handle only numeric input
    const handleVerificationCodeChange = (event) => {
      const value = event.target.value;
      // Only set numeric values and prevent non-numeric characters
      if (/^\d*$/.test(value)) {
        setVerificationCode(value);
      }
    };
  
    const handleSubmitVerification = async () => {
      if (verificationCode) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/verificationSMS/verify`,
            { mobile, verificationCode },
            {
              headers: {
                "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
              },
            }
          );
  
          if (response.data.success) {
            // Reset cooldown time on successful verification
            setCooldownTime(30);
            onVerificationSuccess(); // If the code is valid, call the success function
          } else {
            setAlertType("error");
            setAlertMessage("Invalid verification code.");
          }
        } catch (error) {
          setAlertType("error");
          setAlertMessage("Error verifying the code. Please try again.");
        }
      } else {
        setAlertType("error");
        setAlertMessage("Please enter the verification code.");
      }
    };
  
    // Check if the verification code is at least 6 digits
    const isVerificationCodeValid = verificationCode.length === 6;
  
    return (
      <Box>
        <Typography sx={{ mb: 2 }}>
          To complete your registration, please click on the{" "}
          <strong>Send SMS</strong> button below and provide the verification code
          that is sent to your mobile number.
        </Typography>
  
        {/* SMS Verification Field with button as adornment */}
        <TextField
          label="Enter Code"
          variant="outlined"
          value={verificationCode}
          onChange={handleVerificationCodeChange}
          sx={{ mt: 2, width: "100%", p: 0 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  onClick={sendVerificationSMS}
                  disabled={isCooldownActive} // Disable the button when cooldown is active
                  sx={{
                    color: "#222",
                    m: 0,
                    backgroundColor: "#eee",
                    boxShadow: "none",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                >
                  {isCooldownActive ? (
                    <>
                      Wait {cooldownTime}s
                      <CircularProgress size={16} sx={{ color: "#fff", ml: 1 }} />
                    </>
                  ) : (
                    "Send SMS"
                  )}
                </Button>
  
                <Button
                  variant="contained"
                  onClick={handleSubmitVerification}
                  disabled={isLoading || !isVerificationCodeValid} // Disable Verify if the code is not 6 digits long
                  sx={{
                    color: "white",
                    m: 0,
                    boxShadow: "none",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  Verify
                </Button>
              </InputAdornment>
            ),
          }}
        />
  
        {alertMessage && (
          <Alert severity={alertType} sx={{ mt: 2 }}>
            {alertMessage}
          </Alert>
        )}
      </Box>
    );
  };
  
  export default OnBoarding_SMSLink;
  